import { useState } from 'react'
import {
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Card,
  CardBody,
  Heading,
  Text,
  Button,
  HStack,
  VStack,
  Grid,
  GridItem,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Badge,
} from '@chakra-ui/react'
import { Plus, MoreVertical, FileText, Folder, Tag as TagIcon, Upload } from 'lucide-react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { useThemeColors } from '@/hooks/useThemeColors'

const mockMedia = [
  {
    id: 1,
    name: 'hero-image.jpg',
    type: 'image',
    size: '2.4 MB',
    uploaded: '2 hours ago',
  },
  {
    id: 2,
    name: 'document.pdf',
    type: 'document',
    size: '1.2 MB',
    uploaded: '1 day ago',
  },
]

const mockCategories = [
  { id: 1, name: 'Images', count: 24, color: 'blue' },
  { id: 2, name: 'Documents', count: 12, color: 'green' },
  { id: 3, name: 'Videos', count: 8, color: 'purple' },
]

const mockTags = [
  { id: 1, name: 'hero', color: 'red' },
  { id: 2, name: 'banner', color: 'blue' },
  { id: 3, name: 'logo', color: 'green' },
]

export default function ContentPage() {
  const colors = useThemeColors()
  const [activeTab, setActiveTab] = useState(0)

  return (
    <AdminLayout>
      <Box p={6}>
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between">
            <Box>
              <Heading size="lg" mb={2}>
                Content Management
              </Heading>
              <Text color={colors.text}>Manage your media files, categories, and tags</Text>
            </Box>
          </HStack>

          <Tabs index={activeTab} onChange={setActiveTab}>
            <TabList>
              <Tab>Media Library</Tab>
              <Tab>Categories</Tab>
              <Tab>Tags</Tab>
            </TabList>

            <TabPanels>
              <TabPanel px={0}>
                <Card>
                  <CardBody>
                    <HStack justify="space-between" mb={4}>
                      <Heading size="md">Media Library</Heading>
                      <Button colorScheme="blue" leftIcon={<Upload size={20} />}>
                        Upload Files
                      </Button>
                    </HStack>

                    <Grid templateColumns="repeat(auto-fill, minmax(200px, 1fr))" gap={4}>
                      {mockMedia.map((file) => (
                        <GridItem key={file.id}>
                          <Card>
                            <CardBody>
                              <VStack spacing={3}>
                                <Box
                                  w="full"
                                  h="120px"
                                  bg={colors.sectionBg}
                                  borderRadius="md"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                >
                                  <FileText size={40} color={colors.textMuted} />
                                </Box>
                                <VStack spacing={1} w="full">
                                  <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                                    {file.name}
                                  </Text>
                                  <Text fontSize="xs" color={colors.textSecondary}>
                                    {file.size}
                                  </Text>
                                  <Text fontSize="xs" color={colors.textSecondary}>
                                    {file.uploaded}
                                  </Text>
                                </VStack>
                                <Menu>
                                  <MenuButton as={IconButton} icon={<MoreVertical size={16} />} variant="ghost" size="sm" />
                                  <MenuList>
                                    <MenuItem>Edit</MenuItem>
                                    <MenuItem>Download</MenuItem>
                                    <MenuItem color="red.500">Delete</MenuItem>
                                  </MenuList>
                                </Menu>
                              </VStack>
                            </CardBody>
                          </Card>
                        </GridItem>
                      ))}
                    </Grid>
                  </CardBody>
                </Card>
              </TabPanel>

              <TabPanel px={0}>
                <Card>
                  <CardBody>
                    <HStack justify="space-between" mb={4}>
                      <Heading size="md">Categories</Heading>
                      <Button colorScheme="blue" leftIcon={<Plus size={20} />}>
                        Add Category
                      </Button>
                    </HStack>

                    <Grid templateColumns="repeat(auto-fill, minmax(250px, 1fr))" gap={4}>
                      {mockCategories.map((category) => (
                        <GridItem key={category.id}>
                          <Card>
                            <CardBody>
                              <HStack justify="space-between">
                                <VStack align="flex-start" spacing={1}>
                                  <HStack>
                                    <Folder size={20} />
                                    <Text fontWeight="medium">{category.name}</Text>
                                  </HStack>
                                  <Text fontSize="sm" color={colors.textSecondary}>
                                    {category.count} items
                                  </Text>
                                </VStack>
                                <Badge colorScheme={category.color} variant="subtle">
                                  {category.name}
                                </Badge>
                              </HStack>
                            </CardBody>
                          </Card>
                        </GridItem>
                      ))}
                    </Grid>
                  </CardBody>
                </Card>
              </TabPanel>

              <TabPanel px={0}>
                <Card>
                  <CardBody>
                    <HStack justify="space-between" mb={4}>
                      <Heading size="md">Tags</Heading>
                      <Button colorScheme="blue" leftIcon={<Plus size={20} />}>
                        Add Tag
                      </Button>
                    </HStack>

                    <HStack spacing={3} flexWrap="wrap">
                      {mockTags.map((tag) => (
                        <HStack key={tag.id} bg={colors.sectionBg} px={3} py={2} borderRadius="full">
                          <TagIcon size={16} />
                          <Text fontSize="sm">{tag.name}</Text>
                          <Badge colorScheme={tag.color} variant="subtle" fontSize="xs">
                            {tag.name}
                          </Badge>
                          <IconButton aria-label="Tag actions" icon={<MoreVertical size={16} />} variant="ghost" size="xs" />
                        </HStack>
                      ))}
                    </HStack>
                  </CardBody>
                </Card>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>
      </Box>
    </AdminLayout>
  )
}

